/* ============================================================================
 * BUTTON ARCHITECTURE — Background-only scale pattern
 * ============================================================================
 * ::before pseudo handles background — text/icons stay still during hover/press.
 * isolation: isolate creates stacking context so ::before (z:-1) stays behind.
 * The background layer owns hover expansion so text/icons stay anchored.
 * pointer-events: none on ::before prevents scaled surfaces from hijacking
 * hover hit-testing in dense layouts such as dialog footers.
 * Destructive buttons remain on the stable path; other variants expand.
 * ============================================================================ */

/* --- Base --- */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family-sans);
  font-size: var(--text-md);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
  gap: var(--spacing-2);
  position: relative;
  isolation: isolate;
  user-select: none;
  outline: none;
  text-decoration: none;
  transition:
    box-shadow var(--duration-fast) var(--ease-smooth),
    color var(--duration-fast) var(--ease-smooth);

  /* Rendering optimization for scale transforms */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: box-shadow, color;
}

.button[data-hover-motion='layered'] {
  will-change: transform, box-shadow, color;
}

.button::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  z-index: -1;
  pointer-events: none;
  backface-visibility: hidden;
  transform: translateZ(0);
  transition:
    transform var(--duration-fast) var(--ease-smooth),
    background-color var(--duration-fast) var(--ease-smooth),
    border-color var(--duration-fast) var(--ease-smooth);
}

.button:hover:not(:disabled)::before {
  transition: transform var(--duration-fast) var(--ease-spring);
}

.button:disabled {
  opacity: var(--opacity-disabled);
  cursor: not-allowed;
  box-shadow: none;
}

/* ======================================================================== */
/*                              SIZE VARIANTS                               */
/* ======================================================================== */

/* --- Medium (m) — default --- */
.button[data-size='m'] {
  height: var(--control-height-m);
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--radius-lg);
}

.button[data-size='m'][data-icon-only] {
  width: var(--control-height-m);
  padding: var(--spacing-2);
  border-radius: var(--radius-xl);
}

/* --- Small (s) --- */
.button[data-size='s'] {
  height: var(--control-height-s);
  padding: 0 var(--spacing-3) 0 var(--spacing-2);
  border-radius: var(--radius-lg);
  gap: var(--spacing-2);
}

.button[data-size='s'][data-icon-only] {
  width: var(--control-height-s);
  padding: var(--spacing-1);
  border-radius: var(--radius-lg);
}

/* --- Extra Small (xs) --- */
.button[data-size='xs'] {
  height: var(--control-height-xs);
  padding: 0 var(--spacing-2);
  border-radius: var(--radius-lg);
  font-size: var(--text-xs);
}

.button[data-size='xs'][data-icon-only] {
  width: var(--control-height-xs);
  padding: var(--spacing-1);
  border-radius: var(--radius-lg);
}

/* ======================================================================== */
/*                            VARIANT STYLES                                */
/* ======================================================================== */

/* --- Primary --- */
.button[data-variant='primary'] {
  background-color: transparent;
  color: var(--control-fg-on-theme);
  border: none;
  padding: calc(var(--spacing-2) + var(--border-compensation-1px))
    calc(var(--spacing-4) + var(--border-compensation-1px));
  box-shadow: var(--control-shadow-theme);
}

.button[data-variant='primary']::before {
  background-color: var(--control-bg-theme);
}

.button[data-variant='primary']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: var(--control-bg-theme-hover);
}

.button[data-variant='primary']:hover:not(:disabled) {
  color: var(--control-fg-on-theme-hover);
  box-shadow:
    var(--control-shadow-theme),
    0 0 0 var(--spacing-0p5) var(--control-outline-theme-hover);
}

.button[data-variant='primary']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: var(--control-bg-theme-pressed);
}

.button[data-variant='primary']:active:not(:disabled) {
  box-shadow:
    0 0 0 var(--spacing-0p5) var(--control-outline-theme-pressed),
    inset 0 1px 1px rgba(0, 0, 0, 0.2);
}

/* --- Secondary --- */
.button[data-variant='secondary'] {
  background-color: transparent;
  color: var(--surface-fg-primary);
  border: none;
  padding: calc(var(--spacing-2) + var(--border-compensation-1px))
    calc(var(--spacing-4) + var(--border-compensation-1px));
}

.button[data-variant='secondary']::before {
  background-color: transparent;
  border: 1px solid var(--surface-border-primary);
}

.button[data-variant='secondary']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: var(--control-bg-tertiary-hover);
  border-color: var(--surface-fg-tertiary);
}

.button[data-variant='secondary']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: var(--control-bg-tertiary-pressed);
  border-color: var(--surface-fg-tertiary);
}

.button[data-variant='secondary']:active:not(:disabled) {
  box-shadow: none;
}

/* --- Tertiary (formerly ghost) --- */
.button[data-variant='tertiary'] {
  background: none;
  color: var(--control-fg-tertiary);
  border: 1px solid transparent;
}

.button[data-variant='tertiary']::before {
  background-color: transparent;
}

.button[data-variant='tertiary']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: var(--control-bg-tertiary-hover);
}

.button[data-variant='tertiary']:hover:not(:disabled) {
  color: var(--control-fg-tertiary-hover);
}

.button[data-variant='tertiary']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: var(--control-bg-tertiary-pressed);
}

.button[data-variant='tertiary']:active:not(:disabled) {
  color: var(--control-fg-tertiary-hover);
}

/* --- Outline (portal-specific) --- */
.button[data-variant='outline'] {
  background-color: transparent;
  color: var(--text-accent);
  border: none;
  padding: calc(var(--spacing-2) + var(--border-compensation-1px))
    calc(var(--spacing-4) + var(--border-compensation-1px));
}

.button[data-variant='outline']::before {
  border: 1px solid var(--control-bg-accent);
  background-color: transparent;
}

.button[data-variant='outline']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: color-mix(in oklch, var(--control-bg-accent) 10%, transparent);
}

.button[data-variant='outline']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: color-mix(in oklch, var(--control-bg-accent) 15%, transparent);
}

/* --- Quaternary --- */
.button[data-variant='quaternary'] {
  background: none;
  color: var(--control-fg-quaternary);
}

.button[data-variant='quaternary']:hover:not(:disabled) {
  background-color: var(--control-bg-quaternary-hover);
  color: var(--control-fg-quaternary-hover);
}

.button[data-variant='quaternary']:active:not(:disabled) {
  background-color: var(--control-bg-quaternary-pressed);
  color: var(--control-fg-quaternary-hover);
}

/* --- On-Solid --- */
.button[data-variant='on-solid'] {
  background-color: transparent;
  color: var(--control-fg-on-solid);
  border: 1px solid var(--surface-border-tertiary);
  padding: calc(var(--spacing-2) + var(--border-compensation-1px))
    calc(var(--spacing-3) + var(--border-compensation-1px));
}

.button[data-variant='on-solid']::before {
  background-color: transparent;
}

.button[data-variant='on-solid']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: var(--control-bg-on-solid-hover);
}

.button[data-variant='on-solid']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: var(--control-bg-on-solid-pressed);
}

/* --- Destructive --- */
.button[data-variant='destructive'] {
  background-color: transparent;
  color: var(--control-fg-on-solid);
  border: none;
  padding: calc(var(--spacing-2) + var(--border-compensation-1px))
    calc(var(--spacing-3) + var(--border-compensation-1px));
  box-shadow: var(--surface-shadow-sm);
}

.button[data-variant='destructive']::before {
  background-color: var(--control-bg-alert-solid);
}

.button[data-variant='destructive']:hover:not(:disabled)::before {
  transform: scale(1.02);
  background-color: var(--control-bg-alert-solid-hover);
}

.button[data-variant='destructive']:hover:not(:disabled) {
  box-shadow:
    var(--surface-shadow-sm),
    0 0 0 var(--spacing-0p5) var(--surface-hover-subtle);
}

.button[data-variant='destructive']:active:not(:disabled)::before {
  transform: scale(0.99);
  background-color: var(--control-bg-alert-solid-pressed);
}

.button[data-variant='destructive']:active:not(:disabled) {
  box-shadow: var(--surface-shadow-rich-active);
}

/* ======================================================================== */
/*                          REDUCED MOTION                                  */
/* ======================================================================== */

@media (prefers-reduced-motion: reduce) {
  .button,
  .button::before {
    transition-duration: 0.01ms !important;
  }
}
